import { useEffect, useMemo, useState } from 'react';
import SearchSelect, { type Option } from '../components/SearchSelect';
import LineChart from '../components/LineChart';
import FieldSelector, { type MeasureKey } from '../components/FieldSelector';
import { loadManifest } from '../lib/dataLoader';

export default function StudentAnalysis() {
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [state, setState] = useState<any>(null);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [measure, setMeasure] = useState<MeasureKey>('Net');

  useEffect(() => {
    (async () => {
      const m = await loadManifest();
      setState(m);
      // Build options: students, classes, sections could be searched; primary is students
      const opts: Option[] = Object.values(m.students).map((s: any) => ({ id: s.id, label: s.name, subtitle: [s.className ?? '', s.classLevel ? `${s.classLevel}.sınıf` : ''].filter(Boolean).join(' · ') }));
      setOptions(opts);
      setLoading(false);
    })().catch((e) => {
      console.error(e);
      setLoading(false);
    });
  }, []);

  const chart = useMemo(() => {
    if (!state || !selectedStudentId) return null;
    const m = state as any;
    const student = m.students[selectedStudentId];
    if (!student) return null;
    const times = m.times as { key: string; label: string }[];

    const categories = times.map(t => t.label);
    const series: { name: string; data: number[]; dashed?: boolean }[] = [];
    // Student series for selected fields
    for (const code of Array.from(selectedFields)) {
      series.push({ name: `Öğrenci - ${code}`, data: times.map(t => (student.times[t.key]?.fields?.[code]?.[measure] ?? 0)) });
    }

    // Class and school averages
    const className: string | undefined = student.className;
    const classAgg = className ? m.classes[className] : null;
    const schoolAgg = m.school;

    if (classAgg) for (const code of Array.from(selectedFields)) series.push({ name: `Sınıf Ort. - ${code}`, data: times.map(t => (classAgg.times[t.key]?.fields?.[code]?.[measure] ?? 0)), dashed: true });
    if (schoolAgg) for (const code of Array.from(selectedFields)) series.push({ name: `Okul Ort. - ${code}`, data: times.map(t => (schoolAgg.times[t.key]?.fields?.[code]?.[measure] ?? 0)), dashed: true });

    return { categories, series };
  }, [state, selectedStudentId, selectedFields, measure]);

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-800">Öğrenci Analiz</h1>
      {loading && <div className="text-sm text-slate-600">Veriler yükleniyor...</div>}
      {!loading && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-2 text-sm font-medium">Öğrenci Seç</div>
              <SearchSelect options={options} onSelect={(o) => setSelectedStudentId(o?.id ?? null)} />
            </div>
            <div>
              <div className="mb-2 text-sm font-medium">Alan Seçimi</div>
              <FieldSelector
                fields={state?.fieldsIndex ?? []}
                selected={selectedFields}
                onToggle={(code)=>{
                  const next = new Set(selectedFields);
                  next.has(code) ? next.delete(code) : next.add(code);
                  setSelectedFields(next);
                }}
                measure={measure}
                onMeasureChange={setMeasure}
              />
            </div>
          </div>
          <div className="rounded-xl border bg-white p-4">
            {!chart ? (
              <div className="text-sm text-slate-600">Bir öğrenci ve ders(ler) seçin.</div>
            ) : (
              <LineChart categories={chart.categories} series={chart.series} yName="Net" />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function labelOf(k: SubjectKey) {
  switch (k) {
    case 'Turkce': return 'Türkçe';
    case 'Sosyal': return 'Sosyal Bilimler';
    case 'Matematik': return 'Temel Matematik';
    case 'Fen': return 'Fen Bilimleri';
  }
}

function sumSubjects(subjects: Record<string, number> | undefined, keys: string[], fallback?: number): number {
  if (!subjects) return fallback ?? 0;
  const sum = keys.reduce((a, k) => a + (subjects[k] ?? 0), 0);
  return sum > 0 ? sum : (fallback ?? 0);
}


